import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Popover, PopoverModule } from 'primeng/popover';
import { PefAutofillEvent } from '../pef-autofill';
import { PefCellComponent, PefCellValueChange } from '../pef-cell/pef-cell';
import {
  getLocaleSeparators,
  parseRowTsv,
  serializeRowTsv,
} from '../pef-clipboard';
import { PefProgressiveFill } from '../pef-progressive-fill/pef-progressive-fill';
import {
  PefCell,
  PefData,
  PefRow,
  PefRowType,
  PefSheet,
  resolvePefCell,
} from '../pef-schema.types';

@Component({
  selector: 'lib-pef-sheet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PefCellComponent,
    FormsModule,
    PopoverModule,
    ContextMenuModule,
    PefProgressiveFill,
  ],
  templateUrl: './pef-sheet.html',
  styleUrl: './pef-sheet.css',
})
export class PefSheetComponent {
  readonly sheet = input.required<PefSheet>();
  readonly data = input.required<PefData>();
  readonly loading = input<boolean>(false);
  readonly dirtyKeys = input<ReadonlySet<string>>(new Set());
  /** Plain text everywhere: no inputs, no autofill, no row menu. */
  readonly readonly = input<boolean>(false);

  readonly cellValueChange = output<PefCellValueChange>();
  readonly cellValuesChange = output<PefCellValueChange[]>();
  readonly autofill = output<PefAutofillEvent>();

  private readonly colMenu = viewChild<Popover>('colMenu');
  private readonly rowMenu = viewChild<ContextMenu>('rowMenu');
  protected readonly progFill = viewChild(PefProgressiveFill);

  protected readonly columns = computed(() => this.sheet().columns);
  protected readonly rows = computed(() => this.sheet().rows);

  protected readonly activeColumnId = signal<string | null>(null);

  /** Row targeted by the right-click context menu. */
  protected readonly contextRow = signal<PefRow | null>(null);

  protected readonly rowMenuItems = computed<MenuItem[]>(() => {
    const row = this.contextRow();
    const canPaste = row ? this.rowHasInputCells(row) : false;
    return [
      {
        label: 'Copia riga',
        icon: 'pi pi-copy',
        command: () => this.onCopyRow(),
      },
      {
        label: 'Incolla riga',
        icon: 'pi pi-clipboard',
        disabled: !canPaste,
        command: () => this.onPasteRow(),
      },
    ];
  });

  protected readonly activeColumnOtherCols = computed(() => {
    const activeId = this.activeColumnId();
    return this.columns().filter((c) => c.id !== activeId && !c.standalone);
  });

  protected rowClass(rowType: PefRowType): string {
    return `pef-row pef-row--${rowType}`;
  }

  protected resolveCell(row: PefRow, columnId: string): PefCell {
    return resolvePefCell(this.sheet(), row, columnId);
  }

  protected cellValue(row: PefRow, columnId: string): number | string | null {
    const cell = this.resolveCell(row, columnId);
    const key = cell.valueKey ?? `${this.sheet().id}.${row.id}.${columnId}`;
    return this.data()[key] ?? null;
  }

  protected isCellDirty(row: PefRow, columnId: string): boolean {
    const cell = this.resolveCell(row, columnId);
    const key = cell.valueKey ?? `${this.sheet().id}.${row.id}.${columnId}`;
    return this.dirtyKeys().has(key);
  }

  private labelKey(row: PefRow): string {
    return row.labelInputKey!;
  }

  protected labelValue(row: PefRow): string {
    if (!row.labelInputKey) return row.label;
    const v = this.data()[this.labelKey(row)];
    return v !== null && v !== undefined ? String(v) : '';
  }

  protected onLabelChange(row: PefRow, value: string): void {
    if (!row.labelInputKey) return;
    this.cellValueChange.emit({ valueKey: this.labelKey(row), value });
  }

  protected isLabelDirty(row: PefRow): boolean {
    if (!row.labelInputKey) return false;
    return this.dirtyKeys().has(this.labelKey(row));
  }

  protected rowHasInputCells(row: PefRow): boolean {
    return this.columns().some(
      (col) => resolvePefCell(this.sheet(), row, col.id).type === 'input',
    );
  }

  // -------------------------------------------------------------------------
  // Autofill handlers
  // -------------------------------------------------------------------------

  protected openColumnMenu(colId: string, event: Event): void {
    this.activeColumnId.set(colId);
    this.colMenu()?.toggle(event);
  }

  protected onColumnAutofillToAll(): void {
    const colId = this.activeColumnId();
    if (!colId) return;
    this.colMenu()?.hide();
    this.autofill.emit({
      type: 'column-to-all',
      sheetId: this.sheet().id,
      sourceColumnId: colId,
    });
  }

  protected onColumnAutofillToColumn(targetColumnId: string): void {
    const colId = this.activeColumnId();
    if (!colId) return;
    this.colMenu()?.hide();
    this.autofill.emit({
      type: 'column-to-column',
      sheetId: this.sheet().id,
      sourceColumnId: colId,
      targetColumnId,
    });
  }

  protected onRowFill(rowId: string): void {
    this.autofill.emit({
      type: 'row-fill',
      sheetId: this.sheet().id,
      sourceColumnId: this.columns()[0]?.id ?? '',
      rowId,
    });
  }

  // -------------------------------------------------------------------------
  // Excel-compatible row copy / paste
  // -------------------------------------------------------------------------

  /** Year (non-standalone) columns, the ones exchanged with Excel. */
  private yearColumns() {
    return this.columns().filter((c) => !c.standalone);
  }

  private cellKey(row: PefRow, columnId: string): string {
    const cell = this.resolveCell(row, columnId);
    return cell.valueKey ?? `${this.sheet().id}.${row.id}.${columnId}`;
  }

  protected onRowContext(event: Event, row: PefRow): void {
    // The row menu only copies and pastes cells, so the browser menu is the
    // right one when the sheet cannot be edited.
    if (this.readonly()) return;
    event.preventDefault();
    this.contextRow.set(row);
    this.rowMenu()?.show(event);
  }

  /** Copies every year cell of the active row to the clipboard as TSV. */
  protected async onCopyRow(): Promise<void> {
    const row = this.contextRow();
    if (!row) return;
    const data = this.data();
    const values = this.yearColumns().map((col) => {
      if (this.resolveCell(row, col.id).type === 'empty') return null;
      return data[this.cellKey(row, col.id)] ?? null;
    });
    const tsv = serializeRowTsv(values, this.clipboardLocale());
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      // Clipboard unavailable (e.g. denied permission) — silently ignore.
    }
  }

  /** Pastes clipboard TSV into the active row's input cells, by column order. */
  protected async onPasteRow(): Promise<void> {
    const row = this.contextRow();
    if (!row) return;
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return;
    }
    const parsed = parseRowTsv(
      text,
      getLocaleSeparators(this.clipboardLocale()),
    );
    const cols = this.yearColumns();
    const changes: PefCellValueChange[] = [];
    cols.forEach((col, i) => {
      if (i >= parsed.length) return;
      if (this.resolveCell(row, col.id).type !== 'input') return;
      changes.push({ valueKey: this.cellKey(row, col.id), value: parsed[i] });
    });
    if (changes.length > 0) this.cellValuesChange.emit(changes);
  }

  private clipboardLocale(): string {
    return (typeof navigator !== 'undefined' && navigator.language) || 'it-IT';
  }
}
